/**
 * Sale Invoice mapping — Access `frmReportCashFlowQryBanHang_add`.
 *
 * Pick an ERP sale invoice + a MISA invoice; Save writes the link into
 * `report_cashflow_map_sohoadon_banhang` + upserts the extract join row keyed
 * on misa_ref. Contract/Invoice extract are the only editable text fields.
 */
import { Autocomplete, Box, CircularProgress, Dialog, DialogContent, TextField, Typography } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import {
  useGetCashflowDetailLookupQuery,
  useSaveCashflowMapBanHangMutation,
  type MapBanHang,
} from '../../../../store/api/dashboardApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { FORM_GRID_2COL } from '../../../../theme/responsive'
import { compactDialogContentSx, formatStandard, readonlyFieldSx } from './formHelpers'

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const fmtDate = (v: unknown) => {
  const d = v ? dayjs(String(v)) : null
  return d && d.isValid() ? d.format('DD-MMM-YYYY') : ''
}

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  row: MapBanHang | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function BanHangMapDialog({ open, mode, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [erpInput, setErpInput] = useState('')
  const [misaInput, setMisaInput] = useState('')
  const [erpSearch, setErpSearch] = useState('') // lookup term ERP — tách khỏi input hiển thị
  const [misaSearch, setMisaSearch] = useState('') // lookup term MISA — tách khỏi input hiển thị
  const [erp, setErp] = useState<Row | null>(null)
  const [misa, setMisa] = useState<Row | null>(null)
  const [contractExtract, setContractExtract] = useState('')
  const [invExtract, setInvExtract] = useState('')
  const [save, { isLoading }] = useSaveCashflowMapBanHangMutation()

  // `currentData` (KHÔNG dùng `data`): `data` giữ kết quả của arg TRƯỚC trong lúc
  // query mới đang chạy → view misa nặng (vài giây) + filterOptions={(x)=>x} làm
  // dropdown hiện list của lần search trước như thể "cộng dồn". `currentData`
  // trả undefined khi đang load arg mới → dropdown trống + spinner, hết dòng cũ.
  const { currentData: erpData, isFetching: erpFetching } = useGetCashflowDetailLookupQuery({ kind: 'erp_banhang', q: erpSearch || undefined }, { skip: !open })
  const { currentData: misaData, isFetching: misaFetching } = useGetCashflowDetailLookupQuery({ kind: 'misa_banhang', q: misaSearch || undefined }, { skip: !open })

  const erpLabel = (r: Row) => s(r.filter_txt) || `${s(r.contract_no)} - ${s(r.erp_inv)}`
  const misaLabel = (r: Row) => s(r.filter_txt) || `${s(r.misa_sohoadon)} - ${s(r.sochungtu)}`

  const erpOptions = useMemo(() => erpData?.items ?? [], [erpData])
  const misaOptions = useMemo(() => misaData?.items ?? [], [misaData])
  // Inject dòng đang chọn vào options CHỈ KHI không search (tắt MUI warning lúc
  // idle). Đang gõ text (q active) thì KHÔNG inject — filterOptions={(x)=>x} hiển
  // thị mọi options nên dòng inject cũ sẽ lẫn vào kết quả dù không khớp text
  // (bug "search ra 1 dòng đúng + dòng của lần search trước", 2026-08-03).
  const erpMerged = useMemo(
    () => (!erpSearch && erp && !erpOptions.some((o) => erpLabel(o) === erpLabel(erp)) ? [erp, ...erpOptions] : erpOptions),
    [erp, erpOptions, erpSearch],
  )
  const misaMerged = useMemo(
    () => (!misaSearch && misa && !misaOptions.some((o) => s(o.ref_id) === s(misa.ref_id)) ? [misa, ...misaOptions] : misaOptions),
    [misa, misaOptions, misaSearch],
  )

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      const r = row as unknown as Row
      // Seed maps EVERY field the row may carry — the viewer can pass either a
      // bh-need row (ERP-heavy) or a "Misa Sales" row (MISA-heavy); each side
      // fills from whatever keys exist. ERP side stays blank for a Misa row that
      // isn't mapped yet (contractNo/erpInv empty) → user picks the ERP invoice.
      setErp({
        contract_no: r.contractNo, erp_inv: r.erpInv, account_object_code: r.accountObjectCode,
        cp_code: r.cpCode, short_name: r.shortName, buyers_ref: r.buyersRef, contract_date: r.contractDate,
        erp_inv_amount_customer: r.erpInvAmountCustomer,
        filter_txt: `${s(r.contractNo)} - ${s(r.erpInv)}`,
      })
      setMisa({
        ref_id: r.refId, misa_sohoadon: r.misaSohoadon, sochungtu: r.sochungtu,
        misa_contract: r.misaContract, dien_giai: r.dienGiai,
        erp_cp_code: r.erpCpCode, erp_cp_name: r.erpCpName, khachhang_name: r.khachhangName,
        tab_name: r.tabName, ngaychungtu: r.ngaychungtu, loaitien: r.loaitien,
        tygia: r.tygia, tong_tien_thanh_toan: r.tongTienThanhToan,
        filter_txt: `${s(r.misaSohoadon)} - ${s(r.sochungtu)}`,
      })
      setContractExtract(s(row.contractExtract))
      setInvExtract(s(row.invExtract))
    } else {
      setErp(null)
      setMisa(null)
      setContractExtract('')
      setInvExtract('')
    }
    setErpInput('')
    setMisaInput('')
    setErpSearch('')
    setMisaSearch('')
  }, [open, mode, row])

  // Edit from "Misa Sales": the seeded ERP side only has contract_no/erp_inv from
  // the mapping (no cp_code/snapshot). Auto-lookup the full ERP invoice by its
  // erp_inv and match on (contract_no, erp_inv) → fill the ERP snapshot.
  const needErpLookup =
    mode === 'edit' && !!s(erp?.contract_no) && !!s(erp?.erp_inv) && !s(erp?.cp_code)
  const { data: erpLinkData, isFetching: erpLinkFetching } = useGetCashflowDetailLookupQuery(
    { kind: 'erp_banhang', q: s(erp?.erp_inv) || undefined },
    { skip: !open || !needErpLookup },
  )
  useEffect(() => {
    if (!needErpLookup) return
    const match = (erpLinkData?.items ?? []).find(
      (o) => s(o.contract_no) === s(erp?.contract_no) && s(o.erp_inv) === s(erp?.erp_inv),
    )
    if (match) setErp(match)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erpLinkData, needErpLookup])

  // Same gap on the MISA side: the map-banhang list row only carries ref_id /
  // sochungtu / misa_sohoadon / misa_contract / dien_giai — NOT the MISA snapshot
  // (erp_cp_code, erp_cp_name, khachhang_name, ngaychungtu, loaitien, tygia,
  // tong_tien_thanh_toan). So the right-column readonly fields (VoucherDate,
  // Currency, FX, PaymentAmount, ERP CP Code/Name…) render blank on Edit. Re-fetch
  // the full MISA invoice by its voucher/invoice no and match on ref_id to fill it
  // (mirrors the ERP re-lookup above).
  const needMisaLookup = mode === 'edit' && !!s(misa?.ref_id) && !s(misa?.ngaychungtu)
  const { data: misaLinkData, isFetching: misaLinkFetching } = useGetCashflowDetailLookupQuery(
    { kind: 'misa_banhang', q: (s(misa?.sochungtu) || s(misa?.misa_sohoadon)) || undefined },
    { skip: !open || !needMisaLookup },
  )
  useEffect(() => {
    if (!needMisaLookup) return
    const match = (misaLinkData?.items ?? []).find((o) => s(o.ref_id) === s(misa?.ref_id))
    if (match) setMisa(match)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [misaLinkData, needMisaLookup])

  // Edit seeds the combos synchronously, but the readonly ERP/MISA snapshot fields
  // are hydrated by the re-lookup fetches above (they flash blank otherwise). Show a
  // loading state until whichever enrichment fetch is in flight lands.
  const recordLoading = (needErpLookup && erpLinkFetching) || (needMisaLookup && misaLinkFetching)

  // Enhancement (chị Đẹp request — NOT in Access): once BOTH ERP and MISA invoices
  // are picked, suggest the extract fields from the ERP invoice (contract_no →
  // Contract Extract, erp_inv → Invoice Extract) as matching hints. Works in New
  // AND Edit — runs only on an active combo pick (not on open), so Edit keeps its
  // saved extracts until the user re-picks. Handler-based (cascade in onChange,
  // not useEffect) so it runs in the same tick — see /convert-form anti-pattern #41.
  const suggestExtracts = (erpObj: Row | null, misaObj: Row | null) => {
    if (!s(erpObj?.contract_no) || !s(misaObj?.ref_id)) return
    setContractExtract(s(erpObj?.contract_no))
    setInvExtract(s(erpObj?.erp_inv))
  }
  // Only suggest when the pair was INCOMPLETE before this pick (≥1 of ERP/MISA
  // empty) — completing a mapping, not re-picking on an already-full one (Edit:
  // don't clobber saved extracts). Emptiness = by content (contract_no / ref_id),
  // NOT object existence: the Edit-hydrate seeds a truthy erp/misa object even for
  // a "Misa Sales" row whose ERP side is still blank. New is always incomplete
  // until both picked → suggests on the 2nd pick.
  const wasIncomplete = () => !s(erp?.contract_no) || !s(misa?.ref_id)
  const pickErp = (v: Row | null) => {
    const inc = wasIncomplete()
    setErp(v)
    if (inc) suggestExtracts(v, misa)
  }
  const pickMisa = (v: Row | null) => {
    const inc = wasIncomplete()
    setMisa(v)
    if (inc) suggestExtracts(erp, v)
  }

  const handleSave = async () => {
    if (!canEdit) return
    if (!s(erp?.contract_no)) {
      window.alert('Please choose ERP INVOICE!')
      return
    }
    if (!row?.misaMapId && !s(misa?.ref_id)) {
      window.alert('Please choose MISA INVOICE in the INSERT query!')
      return
    }
    try {
      const res = await save({
        misaMapId: mode === 'edit' ? row?.misaMapId ?? null : null,
        erpInvNo: s(erp?.erp_inv),
        erpContractNo: s(erp?.contract_no),
        // Access btnSave: Me.erp_cp_code = rst!cp_code (ERP combo) vs
        // Me.misa_cp_code = rst!erp_cp_code (MISA combo). Must use cp_code here —
        // account_object_code is a DIFFERENT field (→ erp_misa_code) and using it
        // made the "same Object code" guard reject valid mappings.
        erpCpCode: s(erp?.cp_code),
        misaCpCode: s(misa?.erp_cp_code),
        misaRefId: s(misa?.ref_id),
        contractExtract: contractExtract || null,
        invExtract: invExtract || null,
      }).unwrap()
      snack.success(res.message || 'Successfully Added')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the mapping.')
    }
  }

  // Readonly snapshot field (Access Enabled=NotDefault). col/row = grid position.
  const RO = (label: string, value: string, col: 1 | 2, gridRow: number, numeric = false) => (
    <TextField
      label={label}
      value={value}
      size="small"
      fullWidth
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={numeric ? { style: { textAlign: 'right' } } : undefined}
      sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: col }, gridRow: { sm: gridRow } }}
    />
  )
  const e = erp ?? {}
  const m = misa ?? {}
  // PaymentAmount is a VND amount. When the voucher currency is USD, also show the
  // USD equivalent (= VND / FX) beside it; for VND vouchers show only the VND field.
  const paymentVnd = m.tong_tien_thanh_toan as number | null
  const paymentFx = typeof m.tygia === 'number' ? m.tygia : Number(m.tygia)
  const isUsdVoucher = s(m.loaitien).toUpperCase() === 'USD'
  const paymentUsd =
    isUsdVoucher && paymentVnd != null && Number.isFinite(paymentFx) && paymentFx !== 0
      ? paymentVnd / paymentFx
      : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitleBar
        title="Sale Invoice - Misa Invoice"
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit}
      />
      <DialogContent sx={compactDialogContentSx}>
        {recordLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        /* Access `_add` 2-col: LEFT = ERP invoice (picker + readonly snapshot),
            RIGHT = MISA invoice (picker + readonly snapshot) + editable extracts. */
        <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
          {/* ── Left column: ERP ── */}
          <Autocomplete
            size="small"
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }}
            options={erpMerged}
            loading={erpFetching}
            getOptionLabel={erpLabel}
            isOptionEqualToValue={(o, v) => erpLabel(o) === erpLabel(v)}
            renderOption={(props, o, { index }) => (
              <li {...props} key={`erp-${index}`}>{erpLabel(o)}</li>
            )}
            value={erp}
            onChange={(_, v) => { pickErp(v); setErpSearch('') }}
            inputValue={erpInput}
            onInputChange={(_, v, reason) => { setErpInput(v); if (reason === 'input') setErpSearch(v); else if (reason === 'clear') setErpSearch('') }}
            // Server-search (q = erpSearch, BE ILIKE filter_txt) → tắt client filter
            // để MUI không lọc lại lần 2 theo label (đồng bộ MuaHangMapDialog).
            filterOptions={(x) => x}
            selectOnFocus
            renderInput={(p) => <TextField {...p} label="ERP INVOICE" placeholder="SaleContract, Invoice…" InputLabelProps={{ shrink: true }} />}
          />
          {RO('ERP CP Code', s(e.cp_code), 1, 2)}
          {RO('Misa Object Code', s(e.account_object_code), 1, 3)}
          {RO('Contract No', s(e.contract_no), 1, 4)}
          {RO('Invoice No', s(e.erp_inv), 1, 5)}
          {RO("Buyer'sRef", s(e.buyers_ref), 1, 6)}
          {RO('ERP CP Name', s(e.short_name), 1, 7)}
          {RO('Contract - Date', fmtDate(e.contract_date), 1, 8)}
          {RO('Inv - CustomerAmount', formatStandard(e.erp_inv_amount_customer as number | null), 1, 9, true)}

          {/* ── Right column: MISA ── */}
          <Autocomplete
            size="small"
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } }}
            options={misaMerged}
            loading={misaFetching}
            getOptionLabel={misaLabel}
            isOptionEqualToValue={(o, v) => s(o.ref_id) === s(v.ref_id)}
            renderOption={(props, o, { index }) => (
              <li {...props} key={s(o.ref_id) || `misa-${index}`}>{misaLabel(o)}</li>
            )}
            value={misa}
            onChange={(_, v) => { pickMisa(v); setMisaSearch('') }}
            inputValue={misaInput}
            onInputChange={(_, v, reason) => { setMisaInput(v); if (reason === 'input') setMisaSearch(v); else if (reason === 'clear') setMisaSearch('') }}
            // Server-search (q = misaSearch, BE ILIKE filter_txt) → tắt client filter
            // để MUI không lọc lại lần 2 theo label (đồng bộ MuaHangMapDialog).
            filterOptions={(x) => x}
            selectOnFocus
            renderInput={(p) => <TextField {...p} label="MISA INVOICE" placeholder="Sale, Discount, Return, Voucher…" InputLabelProps={{ shrink: true }} />}
          />
          {RO('Misa Voucher ID', s(m.ref_id), 2, 2)}
          {RO('VoucherNo', s(m.sochungtu), 2, 3)}
          {RO('Invoice No', s(m.misa_sohoadon), 2, 4)}
          {RO('ContractNo', s(m.misa_contract), 2, 5)}
          {RO('ERP CP Code', s(m.erp_cp_code), 2, 6)}
          {RO('Misa Object Name', s(m.erp_cp_name) || s(m.khachhang_name), 2, 7)}
          {RO('VoucherDate', fmtDate(m.ngaychungtu), 2, 8)}
          {RO('Currency', s(m.loaitien), 2, 9)}
          {RO('FX', formatStandard(m.tygia as number | null), 2, 10, true)}
          {/* PaymentAmount (VND) + (USD) on one row, 2 columns. USD only for USD vouchers. */}
          <Box sx={{ display: 'flex', gap: 1, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 11 } }}>
            <TextField
              label="PaymentAmount (VND)"
              value={formatStandard(paymentVnd)}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { textAlign: 'right' } }}
              sx={readonlyFieldSx}
            />
            {isUsdVoucher && (
              <TextField
                label="PaymentAmount (USD)"
                value={formatStandard(paymentUsd)}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { textAlign: 'right' } }}
                sx={readonlyFieldSx}
              />
            )}
          </Box>
          {/* Description: readonly but auto-height to the real text (multiline, no fixed rows). */}
          <TextField
            label="Description"
            value={s(m.dien_giai)}
            size="small"
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ ...readonlyFieldSx, gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 12 } }}
          />
          {/* Editable extracts (Access: right column, bottom). */}
          <TextField
            label="MISA - Contract Extract"
            value={contractExtract}
            onChange={(e2) => setContractExtract(e2.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 13 } }}
          />
          <TextField
            label="MISA - Invoice Extract"
            value={invExtract}
            onChange={(e2) => setInvExtract(e2.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 14 } }}
          />
        </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
